import { useState, useEffect } from 'react';
import { Navbar } from '../components/layout/Navbar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import IndustryArchitectTip from '../components/onboarding/IndustryArchitectTip';
import { useOnboarding } from '../context/OnboardingContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Building2,
  Package,
  Users,
  Target,
  FileText,
  Globe,
  Plus,
  Trash2,
  Save,
  RefreshCw,
  Upload,
  Link,
  CheckCircle,
  AlertCircle,
  Clock,
  Layers,
  Sparkles,
  BookOpen,
  Settings2,
  Eye,
  Image,
  Wand2,
  Check,
  X,
  ChevronRight,
  Compass,
  Calendar,
  Layers3,
  Lightbulb
} from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { SectionSuggestButton } from '../components/industry-architect/SectionSuggestButton';

export default function IndustryArchitect() {
  const { user, isAdmin, effectiveUser, isViewingAsOther } = useAuth();
  const { refresh: refreshOnboarding } = useOnboarding();
  const api = useApi();
  const [activeTab, setActiveTab] = useState('knowledge');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [contextStatus, setContextStatus] = useState(null);
  
  // Seller Context Profile state
  const [profile, setProfile] = useState({
    seller_industry: 'Corporate Apparel',
    company_name: '',
    value_proposition: '',
    products: [],
    product_tiers: [],
    target_segments: ['hospitality', 'restaurants', 'airlines'],
    buyer_roles: [],
    core_problems: [],
    trigger_patterns: [],
    value_drivers: [],
    differentiators: [],
    department_solutions: {},
    positioning: 'premium',
    brand_voice: 'professional',
    detection_mode: 'company'  // Phase 2 — Tri-Mode Detection (default)
  });
  
  // Knowledge Sources state
  const [knowledgeSources, setKnowledgeSources] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [newUrl, setNewUrl] = useState({ name: '', url: '' });
  
  // Extraction Engine state (Section 1)
  const [extracting, setExtracting] = useState(false);
  const [extractionDraft, setExtractionDraft] = useState(null);
  const [selectedSourcesForExtraction, setSelectedSourcesForExtraction] = useState([]);
  const [showExtractionModal, setShowExtractionModal] = useState(false);

  // Phase 2 — Tri-Mode Detection state
  // Holds the LLM-suggested mode from the most recent extraction (NEVER auto-saves)
  const [detectionModeSuggestion, setDetectionModeSuggestion] = useState(null);
  // Holds the user's pending choice in the UI (compared against profile.detection_mode to determine "draft" status)
  const [pendingDetectionMode, setPendingDetectionMode] = useState(null);

  // Safely coerce extraction values (strings, objects with name/title/role/segmentName) to display strings
  const toDisplayString = (v) => {
    if (v == null) return '';
    if (typeof v === 'string') return v;
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    if (typeof v === 'object') {
      return v.name || v.title || v.role || v.segmentName || v.driver || v.problem || v.value || v.text || v.label || '';
    }
    return String(v);
  };

  // Phase 2 — "Why this mode?" — Build a real-world deal example tailored to the seller context.
  // Returns { trigger, action } for the given mode. Personalises using profile.target_segments and profile.seller_industry when available.
  const buildModeExample = (mode, p) => {
    const segs = (p?.target_segments || []).map(s => (typeof s === 'string' ? s : (s?.segmentName || s?.name || ''))).filter(Boolean);
    const industry = (p?.seller_industry || '').toLowerCase();
    const segLower = segs.join(' ').toLowerCase();

    const isHospitality = /hospitality|hotel|resort|restaurant|airline|cruise|spa/.test(segLower) || /hospitality|apparel|uniform/.test(industry);
    const isRealEstate = /real estate|realtor|property|brokerage/.test(segLower + ' ' + industry);
    const isUniformService = /uniform|workwear|apparel|laundry|linen/.test(segLower + ' ' + industry);
    const isSaaS = /saas|software|cloud|platform/.test(industry) && !isHospitality;

    if (mode === 'company') {
      if (isHospitality) {
        return { trigger: '"Marriott San Diego announces $40M renovation"', action: 'Outreach to GM about uniform refresh tied to re-opening' };
      }
      if (isRealEstate) {
        return { trigger: '"Vancouver-based exec relocates to Calgary HQ"', action: 'Outreach to broker before house listing decision' };
      }
      if (isUniformService) {
        return { trigger: '"Aramark wins 3-property hotel contract in Miami"', action: 'Position alternative supplier or collab partner before vendor lock-in' };
      }
      if (isSaaS) {
        return { trigger: '"Acme Corp hires new VP of RevOps"', action: 'Outreach within first 30 days — peak buying window' };
      }
      return { trigger: '"Marriott San Diego announces $40M renovation"', action: 'Outreach to GM about your service tied to the re-opening' };
    }

    if (mode === 'events') {
      if (isHospitality || isUniformService) {
        return { trigger: '"HITEC 2026 confirmed for Houston, June 16-19"', action: 'Sponsor or attend — 200+ hotel Ops Directors in one room' };
      }
      if (isRealEstate) {
        return { trigger: '"Inman Connect Las Vegas attendee list released"', action: 'Pre-conference outreach to top 50 brokerages presenting' };
      }
      if (isSaaS) {
        return { trigger: '"SaaStr Annual sponsor list announced"', action: 'Identify ICP-matching attendees before their booking confirms' };
      }
      return { trigger: '"Trade show with 500+ ICP-matching attendees confirmed"', action: 'Pre-event outreach window — book 30 days before' };
    }

    // hybrid
    if (isHospitality || isUniformService) {
      return { trigger: '"Aramark CFO speaking at HITEC Houston"', action: 'Account watch + event prep: meet at booth, follow-up on renewal' };
    }
    if (isRealEstate) {
      return { trigger: '"Top broker keynote at Inman + recent team expansion"', action: 'Combined account + event play in one outreach sequence' };
    }
    if (isSaaS) {
      return { trigger: '"Target CRO announced as SaaStr panelist"', action: 'Account-trigger + event-trigger compound — highest conversion path' };
    }
    return { trigger: '"Target CFO speaking at your industry\'s top event"', action: 'Combine account watch + event prep for compound outreach' };
  };
  
  // Input helpers for array fields
  const [newProduct, setNewProduct] = useState({ name: '', category: '', description: '', value_drivers: [] });
  const [newTier, setNewTier] = useState({ name: '', description: '', price_range: '', ideal_for: '' });
  const [newBuyerRole, setNewBuyerRole] = useState({ title: '', priorities: [], pain_points: [] });
  const [newProblem, setNewProblem] = useState('');
  const [newTriggerPattern, setNewTriggerPattern] = useState('');
  const [newValueDriver, setNewValueDriver] = useState('');
  const [newDifferentiator, setNewDifferentiator] = useState('');
  const [newSegment, setNewSegment] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [profileRes, sourcesRes] = await Promise.all([
        api.get('/sales-intelligence/seller-context'),
        api.get('/sales-intelligence/knowledge-sources')
      ]);
      
      if (profileRes?.profile) {
        setProfile(prev => ({ ...prev, ...profileRes.profile }));
      }
      
      if (Array.isArray(sourcesRes)) {
        setKnowledgeSources(sourcesRes);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put('/sales-intelligence/seller-context', profile);
      toast.success('Seller Context Profile saved - AI modules will now use this context');
      // Refresh context status
      loadContextStatus();
      // Refresh onboarding state so the Industry Architect tip auto-hides
      // and the signature nudge (if applicable) fires for first-time users.
      try { await refreshOnboarding(); } catch (_) {}
    } catch (error) {
      toast.error(error.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  // Load context status
  const loadContextStatus = async () => {
    try {
      const res = await api.get('/style-detection/seller-context-status');
      setContextStatus(res);
    } catch (error) {
      console.warn('Could not load context status:', error);
    }
  };

  // Reprocess existing signals with current seller context
  const reprocessSignals = async () => {
    setReprocessing(true);
    try {
      const res = await api.post('/style-detection/reprocess-with-seller-context', {});
      toast.success(`Reprocessed ${res.results?.total || 0} signals with ${res.detection_mode} mode`);
      setContextStatus(prev => ({ ...prev, detection_mode: res.detection_mode }));
    } catch (error) {
      toast.error(error.message || 'Failed to reprocess signals');
    } finally {
      setReprocessing(false);
    }
  };

  // Load context status on mount
  useEffect(() => {
    loadContextStatus();
  }, []);

  // Product handlers
  const addProduct = () => {
    if (!newProduct.name) return;
    setProfile(prev => ({
      ...prev,
      products: [...prev.products, { ...newProduct, value_drivers: newProduct.value_drivers || [] }]
    }));
    setNewProduct({ name: '', category: '', description: '', value_drivers: [] });
  };

  const removeProduct = (index) => {
    setProfile(prev => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== index)
    }));
  };

  // Product Tier handlers
  const addTier = () => {
    if (!newTier.name) return;
    setProfile(prev => ({
      ...prev,
      product_tiers: [...prev.product_tiers, { ...newTier }]
    }));
    setNewTier({ name: '', description: '', price_range: '', ideal_for: '' });
  };

  const removeTier = (index) => {
    setProfile(prev => ({
      ...prev,
      product_tiers: prev.product_tiers.filter((_, i) => i !== index)
    }));
  };

  // Buyer Role handlers
  const addBuyerRole = () => {
    if (!newBuyerRole.title) return;
    setProfile(prev => ({
      ...prev,
      buyer_roles: [...prev.buyer_roles, { ...newBuyerRole }]
    }));
    setNewBuyerRole({ title: '', priorities: [], pain_points: [] });
  };

  const removeBuyerRole = (index) => {
    setProfile(prev => ({
      ...prev,
      buyer_roles: prev.buyer_roles.filter((_, i) => i !== index)
    }));
  };

  // Simple array field handlers
  const addToArray = (field, value, setValue) => {
    if (!value.trim()) return;
    setProfile(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), value.trim()]
    }));
    setValue('');
  };

  const removeFromArray = (field, index) => {
    setProfile(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  // Knowledge Source handlers
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are allowed');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name.replace('.pdf', ''));
    
    try {
      const result = await api.upload('/sales-intelligence/knowledge-sources/upload-pdf', formData);
      toast.success('PDF uploaded and processing started');
      // Refresh sources after delay
      setTimeout(loadData, 2000);
    } catch (error) {
      toast.error(error.message || 'Failed to upload PDF');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const addUrlSource = async () => {
    if (!newUrl.name || !newUrl.url) {
      toast.error('Please provide both name and URL');
      return;
    }
    
    setUploading(true);
    try {
      await api.post('/sales-intelligence/knowledge-sources/add-url', newUrl);
      toast.success('URL added and processing started');
      setNewUrl({ name: '', url: '' });
      setTimeout(loadData, 2000);
    } catch (error) {
      toast.error(error.message || 'Failed to add URL');
    } finally {
      setUploading(false);
    }
  };

  const deleteSource = async (sourceId) => {
    if (!confirm('Delete this knowledge source?')) return;
    
    try {
      await api.delete(`/sales-intelligence/knowledge-sources/${sourceId}`);
      toast.success('Knowledge source deleted');
      setKnowledgeSources(prev => prev.filter(s => s.id !== sourceId));
    } catch (error) {
      toast.error(error.message || 'Failed to delete source');
    }
  };

  const reprocessSource = async (sourceId) => {
    try {
      await api.post(`/sales-intelligence/knowledge-sources/${sourceId}/reprocess`);
      toast.success('Reprocessing started');
      setTimeout(loadData, 2000);
    } catch (error) {
      toast.error(error.message || 'Failed to reprocess');
    }
  };

  // ==================== EXTRACTION ENGINE (Section 1) ====================
  
  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name);
    
    try {
      const result = await api.upload('/sales-intelligence/knowledge-sources/upload-image', formData);
      toast.success(`Image "${result.name}" uploaded successfully`);
      await loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to upload image');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };
  
  // Toggle source selection for extraction
  const toggleSourceSelection = (sourceId) => {
    setSelectedSourcesForExtraction(prev => 
      prev.includes(sourceId) 
        ? prev.filter(id => id !== sourceId)
        : [...prev, sourceId]
    );
  };
  
  // Poll an extraction job until complete (or error/timeout)
  const pollExtractionJob = async (jobId, maxAttempts = 60) => {
    for (let i = 0; i < maxAttempts; i++) {
      // Wait 3s between polls
      await new Promise(resolve => setTimeout(resolve, 3000));
      try {
        const status = await api.get(`/sales-intelligence/knowledge-sources/extract-job/${jobId}`);
        if (status.status === 'complete') {
          return status.result;
        }
        if (status.status === 'error') {
          throw new Error(status.error || 'Extraction job failed');
        }
        // else still processing, continue polling
      } catch (err) {
        // 404 / network / etc.
        if (i > 5) throw err;
      }
    }
    throw new Error('Extraction timed out after 3 minutes');
  };

  // Extract intelligence from selected sources
  const extractFromSources = async () => {
    if (selectedSourcesForExtraction.length === 0) {
      toast.error('Please select at least one knowledge source');
      return;
    }
    
    setExtracting(true);
    setExtractionDraft(null);
    
    try {
      let result;
      if (selectedSourcesForExtraction.length === 1) {
        result = await api.post(`/sales-intelligence/knowledge-sources/${selectedSourcesForExtraction[0]}/extract`);
      } else {
        // Multi-source returns a job_id; poll for completion
        const jobResp = await api.post('/sales-intelligence/knowledge-sources/extract-multiple', selectedSourcesForExtraction);
        if (jobResp.job_id) {
          toast.info(`Extracting from ${selectedSourcesForExtraction.length} sources... this may take 1-2 minutes`);
          result = await pollExtractionJob(jobResp.job_id);
        } else {
          result = jobResp;
        }
      }
      
      if (!result || result.extractionStatus === 'error') {
        toast.error(result?.error || 'Extraction failed');
        return;
      }
      
      if (result.extractionStatus === 'no_data_found') {
        toast.warning('No usable data found in the selected source(s)');
        return;
      }
      
      setExtractionDraft(result);
      setShowExtractionModal(true);
      toast.success('Extraction complete - Review draft suggestions');
      
    } catch (error) {
      toast.error(error.message || 'Failed to extract intelligence');
    } finally {
      setExtracting(false);
    }
  };
  
  // Apply a specific section from extraction draft to profile
  const applyDraftSection = (section, data) => {
    switch (section) {
      case 'sellerContext':
        setProfile(prev => ({
          ...prev,
          company_name: data.companyName || prev.company_name,
          seller_industry: data.sellerIndustry || prev.seller_industry,
          value_proposition: data.valueProposition || prev.value_proposition,
          brand_voice: data.brandVoice || prev.brand_voice,
          positioning: data.marketPositioning || prev.positioning
        }));
        toast.success('Seller Context applied - Remember to save!');
        break;
      case 'products':
        if (data && data.length > 0) {
          const newProducts = data.map(p => 
            typeof p === 'string' 
              ? { name: p, category: '', description: '', value_drivers: [] }
              : { name: p.name || '', category: p.category || '', description: p.description || '', value_drivers: p.value_drivers || [] }
          );
          setProfile(prev => ({
            ...prev,
            products: [...prev.products, ...newProducts]
          }));
          toast.success(`${newProducts.length} products added - Remember to save!`);
        }
        break;
      case 'productTiers':
        if (data && data.length > 0) {
          const newTiers = data.map(t => 
            typeof t === 'string'
              ? { name: t, description: '', price_range: '', ideal_for: '' }
              : { name: t.name || '', description: t.description || '', price_range: t.price_range || t.priceRange || '', ideal_for: t.ideal_for || t.idealFor || '' }
          );
          setProfile(prev => ({
            ...prev,
            product_tiers: [...prev.product_tiers, ...newTiers]
          }));
          toast.success(`${newTiers.length} tiers added - Remember to save!`);
        }
        break;
      case 'targetSegments':
        if (data && data.length > 0) {
          const segmentStrings = data.map(toDisplayString).filter(s => s && !s.startsWith('[NEEDS REVIEW') && !s.startsWith('[NOT FOUND'));
          if (segmentStrings.length > 0) {
            setProfile(prev => ({
              ...prev,
              target_segments: [...new Set([...prev.target_segments, ...segmentStrings])]
            }));
            toast.success('Target segments updated - Remember to save!');
          } else {
            toast.info('No usable target segments found in draft');
          }
        }
        break;
      case 'buyerRoles':
        if (data && data.length > 0) {
          const newRoles = data.map(r => {
            if (typeof r === 'string') return { title: r, priorities: [], pain_points: [] };
            const title = r.title || r.role || '';
            return { title, priorities: r.priorities || [], pain_points: r.pain_points || r.painPoints || [] };
          }).filter(r => r.title && !r.title.startsWith('[NEEDS REVIEW') && !r.title.startsWith('[NOT FOUND'));
          if (newRoles.length > 0) {
            setProfile(prev => ({
              ...prev,
              buyer_roles: [...prev.buyer_roles, ...newRoles]
            }));
            toast.success(`${newRoles.length} buyer roles added - Remember to save!`);
          } else {
            toast.info('No usable buyer roles found in draft');
          }
        }
        break;
      case 'valueDrivers':
        if (data && data.length > 0) {
          const drivers = data.map(toDisplayString).filter(s => s && !s.startsWith('[NEEDS REVIEW') && !s.startsWith('[NOT FOUND'));
          if (drivers.length > 0) {
            setProfile(prev => ({
              ...prev,
              value_drivers: [...new Set([...prev.value_drivers, ...drivers])]
            }));
            toast.success('Value drivers updated - Remember to save!');
          } else {
            toast.info('No usable value drivers found in draft');
          }
        }
        break;
      case 'problemsSolved':
        if (data && data.length > 0) {
          const problems = data.map(toDisplayString).filter(s => s && !s.startsWith('[NEEDS REVIEW') && !s.startsWith('[NOT FOUND'));
          if (problems.length > 0) {
            setProfile(prev => ({
              ...prev,
              core_problems: [...new Set([...prev.core_problems, ...problems])]
            }));
            toast.success('Core problems updated - Remember to save!');
          } else {
            toast.info('No usable core problems found in draft');
          }
        }
        break;
      case 'differentiators':
        if (data && data.length > 0) {
          const diffs = data.map(toDisplayString).filter(s => s && !s.startsWith('[NEEDS REVIEW') && !s.startsWith('[NOT FOUND'));
          if (diffs.length > 0) {
            setProfile(prev => ({
              ...prev,
              differentiators: [...new Set([...prev.differentiators, ...diffs])]
            }));
            toast.success('Differentiators updated - Remember to save!');
          } else {
            toast.info('No usable differentiators found in draft');
          }
        }
        break;
      default:
        toast.info('Section not mapped');
    }
  };
  
  // Apply all draft sections at once
  const applyAllDraftSections = () => {
    if (!extractionDraft) return;
    
    if (extractionDraft.sellerContext) {
      applyDraftSection('sellerContext', extractionDraft.sellerContext);
    }
    if (extractionDraft.products?.length > 0) {
      applyDraftSection('products', extractionDraft.products);
    }
    if (extractionDraft.productTiers?.length > 0) {
      applyDraftSection('productTiers', extractionDraft.productTiers);
    }
    if (extractionDraft.targetSegments?.length > 0) {
      applyDraftSection('targetSegments', extractionDraft.targetSegments);
    }
    if (extractionDraft.buyerRoles?.length > 0) {
      applyDraftSection('buyerRoles', extractionDraft.buyerRoles);
    }
    if (extractionDraft.valueDrivers?.length > 0) {
      applyDraftSection('valueDrivers', extractionDraft.valueDrivers);
    }
    if (extractionDraft.problemsSolved?.length > 0) {
      applyDraftSection('problemsSolved', extractionDraft.problemsSolved);
    }
    if (extractionDraft.keyDifferentiators?.length > 0) {
      applyDraftSection('differentiators', extractionDraft.keyDifferentiators);
    }
    
    // Phase 2 — Pre-select suggested Detection Mode (NEVER auto-saves)
    if (extractionDraft.detectionModeSuggestion?.suggestedMode) {
      const sug = extractionDraft.detectionModeSuggestion;
      acceptModeSuggestion(sug.suggestedMode, sug.rationale, sug.confidence);
    }
    
    setShowExtractionModal(false);
    toast.success('All applicable sections applied to profile - Review and Save to confirm!');
  };
  
  // Close extraction modal and discard draft
  const discardExtractionDraft = () => {
    setShowExtractionModal(false);
    setExtractionDraft(null);
    setSelectedSourcesForExtraction([]);
    toast.info('Draft discarded - no changes made');
  };

  // ==================== PHASE 2 — TRI-MODE DETECTION ====================
  // Pre-select user's pending choice from extraction draft when modal closes (suggestion only)
  const acceptModeSuggestion = (mode, rationale, confidence) => {
    setDetectionModeSuggestion({ mode, rationale, confidence });
    setPendingDetectionMode(mode);
    toast.info(`Detection Mode pre-selected: ${mode.toUpperCase()} — review on Detection Mode tab and Save to confirm`);
  };

  // User picks a mode (does not save)
  const selectDetectionMode = (mode) => {
    setPendingDetectionMode(mode);
  };

  // Save the chosen detection mode (additive — touches only this field)
  const saveDetectionMode = async () => {
    const modeToSave = pendingDetectionMode || profile.detection_mode || 'company';
    setSaving(true);
    try {
      const updated = { ...profile, detection_mode: modeToSave };
      await api.put('/sales-intelligence/seller-context', updated);
      setProfile(updated);
      setPendingDetectionMode(null);  // No longer "draft"
      toast.success(`Detection Mode saved: ${modeToSave.toUpperCase()}`);
      loadContextStatus();
    } catch (error) {
      toast.error(error.message || 'Failed to save Detection Mode');
    } finally {
      setSaving(false);
    }
  };

  // Reset to currently-saved mode (discard pending)
  const resetDetectionMode = () => {
    setPendingDetectionMode(null);
    toast.info('Reset to saved mode');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'indexed':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" /> Indexed</Badge>;
      case 'processing':
        return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3 mr-1 animate-pulse" /> Processing</Badge>;
      case 'error':
        return <Badge className="bg-red-100 text-red-700"><AlertCircle className="w-3 h-3 mr-1" /> Error</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F7]">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <RefreshCw className="h-8 w-8 animate-spin text-[#B89B5E]" />
        </div>
      </div>
    );
  }

  // Any logged-in user can edit their own profile; Admin can also edit when viewing as another user
  const canEdit = true;

  return (
    <div className="min-h-screen bg-[#FAF9F7]" data-testid="industry-architect-page">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* First-time user inline tip — auto-hides for existing users
            and once the user saves a non-empty Industry Architect profile. */}
        <div className="mb-6">
          <IndustryArchitectTip />
        </div>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-[#B89B5E]/10">
              <Sparkles className="h-6 w-6 text-[#B89B5E]" />
            </div>
            <div>
              <h1 className="text-2xl font-serif text-[#1A1A1A]">Industry Architect</h1>
              <p className="text-sm text-muted-foreground">
                Configure your AI sales intelligence training center
              </p>
            </div>
          </div>
        </div>

        {/* View As User Banner */}
        {isViewingAsOther && effectiveUser && (
          <div className="bg-amber-100 border border-amber-300 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-amber-500 text-white">
                <Eye className="w-5 h-5" />
              </div>
              <div>
                <p className="font-medium text-amber-900">
                  Configuring settings for: <span className="font-semibold">{effectiveUser.name}</span>
                </p>
                <p className="text-sm text-amber-700">
                  Changes will apply to {effectiveUser.name}'s AI intelligence and market scanning preferences
                </p>
              </div>
            </div>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-[#E8E6E1]">
            <TabsTrigger value="knowledge" className="data-[state=active]:bg-[#1A1A1A] data-[state=active]:text-white">
              <BookOpen className="w-4 h-4 mr-2" />
              Knowledge Sources
            </TabsTrigger>
            <TabsTrigger value="detection-mode" className="data-[state=active]:bg-[#1A1A1A] data-[state=active]:text-white" data-testid="tab-detection-mode">
              <Compass className="w-4 h-4 mr-2" />
              Detection Mode
            </TabsTrigger>
            <TabsTrigger value="seller-context" className="data-[state=active]:bg-[#1A1A1A] data-[state=active]:text-white">
              <Building2 className="w-4 h-4 mr-2" />
              Seller Context
            </TabsTrigger>
            <TabsTrigger value="products" className="data-[state=active]:bg-[#1A1A1A] data-[state=active]:text-white">
              <Package className="w-4 h-4 mr-2" />
              Products & Tiers
            </TabsTrigger>
            <TabsTrigger value="buyers" className="data-[state=active]:bg-[#1A1A1A] data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" />
              Buyer Intelligence
            </TabsTrigger>
          </TabsList>

          {/* ==================== PHASE 2 — DETECTION MODE TAB ==================== */}
          <TabsContent value="detection-mode" className="space-y-6">
            {(() => {
              const savedMode = profile.detection_mode || 'company';
              const selectedMode = pendingDetectionMode || savedMode;
              const isDraft = pendingDetectionMode !== null && pendingDetectionMode !== savedMode;
              const suggestedMode = detectionModeSuggestion?.mode;

              const modes = [
                {
                  id: 'company',
                  icon: Building2,
                  title: 'Company Mode',
                  tagline: 'Track specific companies, properties, and accounts.',
                  bestFor: 'Account-based selling • B2B services • Named-account sales',
                  signalTypes: [
                    'M&A activity, ownership changes, acquisitions',
                    'New property openings, renovations, expansions',
                    'Leadership changes (GM, HR Director, Ops Director)',
                    'Distress events (foreclosure, bankruptcy, RIF)',
                    'RFPs, vendor switches, contract renewals'
                  ],
                  dataSource: 'Google News • SerpAPI • Apollo.io enrichment',
                  example: buildModeExample('company', profile)
                },
                {
                  id: 'events',
                  icon: Calendar,
                  title: 'Events Mode',
                  tagline: 'Track trade shows, conferences, and industry gatherings.',
                  bestFor: 'Sponsorship sales • Exhibition revenue • Demand-gen for in-person',
                  signalTypes: [
                    'Upcoming trade shows in target cities',
                    'Conferences with attendee lists matching ICP',
                    'Hospitality industry events (HITEC, AHLA, etc.)',
                    'Travel/hospitality demand spikes around events',
                    'Speaker/sponsor announcements signaling intent'
                  ],
                  dataSource: 'SerpAPI (event queries) • Source-agnostic adapter (Eventbrite/PredictHQ ready)',
                  example: buildModeExample('events', profile)
                },
                {
                  id: 'hybrid',
                  icon: Layers3,
                  title: 'Hybrid Mode',
                  tagline: 'Track both companies AND events together.',
                  bestFor: 'Multi-channel sales • Broader discovery • Maximum signal coverage',
                  signalTypes: [
                    'Everything from Company Mode',
                    'Everything from Events Mode',
                    'Cross-signal correlation (target attending target event)',
                    'Higher signal volume — recommend tighter Quality Filter (Phase 4)'
                  ],
                  dataSource: 'All sources combined',
                  example: buildModeExample('hybrid', profile)
                }
              ];

              return (
                <>
                  {/* Header */}
                  <Card className="bg-white border-[#E8E6E1]">
                    <CardHeader>
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Compass className="h-5 w-5 text-[#B89B5E]" />
                            Detection Mode
                          </CardTitle>
                          <CardDescription className="mt-1">
                            Tell the engine where signals should come from. Phase 3 (Signal Generation) will only run for the mode you confirm here.
                          </CardDescription>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          {isDraft ? (
                            <Badge className="bg-amber-100 text-amber-800 border border-amber-300" data-testid="mode-status-badge">
                              <AlertCircle className="w-3 h-3 mr-1" /> Draft (unsaved)
                            </Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-700 border border-green-300" data-testid="mode-status-badge">
                              <CheckCircle className="w-3 h-3 mr-1" /> Saved
                            </Badge>
                          )}
                          <span className="text-xs text-muted-foreground">
                            Currently saved: <strong>{savedMode.toUpperCase()}</strong>
                          </span>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>

                  {/* Suggestion banner — shown only if a recent extraction proposed a mode */}
                  {suggestedMode && (
                    <Card className="border-[#B89B5E]/30 bg-[#B89B5E]/5" data-testid="mode-suggestion-banner">
                      <CardContent className="pt-4">
                        <div className="flex items-start gap-3">
                          <Lightbulb className="h-5 w-5 text-[#B89B5E] mt-0.5 flex_shrink-0" />
                          <div className="flex-1">
                            <p className="text-sm font-medium">
                              Suggested by latest extraction: <strong className="uppercase">{suggestedMode}</strong>
                              {detectionModeSuggestion.confidence && (
                                <span className="ml-2 text-xs text-muted-foreground">({detectionModeSuggestion.confidence} confidence)</span>
                              )}
                            </p>
                            {detectionModeSuggestion.rationale && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {detectionModeSuggestion.rationale}
                              </p>
                            )}
                            <p className="text-xs text-[#B89B5E] mt-2">
                              This is a suggestion only. Click any mode below to override. Nothing saves until you click "Save Detection Mode".
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Mode Cards */}
                  <div className="space-y-4">
                    {modes.map((m) => {
                      const Icon = m.icon;
                      const isSelected = selectedMode === m.id;
                      const isSuggested = suggestedMode === m.id;
                      const isCurrentlySaved = savedMode === m.id;

                      return (
                        <Card
                          key={m.id}
                          data-testid={`mode-card-${m.id}`}
                          className={`cursor-pointer transition-all border-2 ${
                            isSelected
                              ? 'border-[#B89B5E] bg-[#B89B5E]/5 shadow-md'
                              : 'border-[#E8E6E1] hover:border-[#B89B5E]/40'
                          }`}
                          onClick={() => selectDetectionMode(m.id)}
                        >
                          <CardContent className="pt-5">
                            <div className="flex items-start gap-4">
                              {/* Radio indicator */}
                              <div className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                isSelected ? 'border-[#B89B5E] bg-[#B89B5E]' : 'border-[#D4CFC4]'
                              }`}>
                                {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                              </div>

                              {/* Icon */}
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                isSelected ? 'bg-[#B89B5E] text-white' : 'bg-stone-100 text-stone-600'
                              }`}>
                                <Icon className="h-5 w-5" />
                              </div>

                              {/* Body */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h3 className="font-semibold text-base">{m.title}</h3>
                                  {isSuggested && (
                                    <Badge className="bg-[#B89B5E] text-white border-[#B89B5E]" data-testid={`suggested-badge-${m.id}`}>
                                      <Sparkles className="w-3 h-3 mr-1" /> Suggested
                                    </Badge>
                                  )}
                                  {isCurrentlySaved && !isDraft && (
                                    <Badge variant="outline" className="text-green-700 border-green-300">
                                      <CheckCircle className="w-3 h-3 mr-1" /> Currently saved
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground mt-1">{m.tagline}</p>

                                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                  <div>
                                    <p className="text-xs font-medium uppercase tracking-wide text-stone-500 mb-1">Best for</p>
                                    <p className="text-stone-700">{m.bestFor}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs font-medium uppercase tracking-wide text-stone-500 mb-1">Data sources</p>
                                    <p className="text-stone-700">{m.dataSource}</p>
                                  </div>
                                </div>

                                <div className="mt-3">
                                  <p className="text-xs font-medium uppercase tracking-wide text-stone-500 mb-1">Signal types this mode generates</p>
                                  <ul className="text-sm text-stone-700 list-disc list-inside space-y-0.5">
                                    {m.signalTypes.map((s, i) => (
                                      <li key={i}>{s}</li>
                                    ))}
                                  </ul>
                                </div>

                                {/* Why this mode? — real-world deal example */}
                                {m.example && (
                                  <div className="mt-3 p-3 rounded-md bg-stone-50 border border-stone-200" data-testid={`why-this-mode-${m.id}`}>
                                    <p className="text-xs font-medium uppercase tracking-wide text-[#B89B5E] mb-1 flex items-center gap-1">
                                      <Lightbulb className="w-3 h-3" /> Why this mode?
                                    </p>
                                    <p className="text-sm text-stone-700">
                                      <span className="italic text-stone-600">{m.example.trigger}</span>
                                      <span className="text-stone-400"> → </span>
                                      <span className="font-medium">{m.example.action}</span>
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>

                  {/* Action footer */}
                  <Card className="bg-stone-50 border-[#E8E6E1]">
                    <CardContent className="pt-5">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="text-sm">
                          {isDraft ? (
                            <p className="text-amber-700">
                              <strong>Draft:</strong> {selectedMode.toUpperCase()} selected but not saved. Click <strong>Save Detection Mode</strong> to confirm.
                            </p>
                          ) : (
                            <p className="text-stone-600">
                              No pending changes. Saved mode is <strong>{savedMode.toUpperCase()}</strong>.
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            onClick={resetDetectionMode}
                            disabled={!isDraft}
                            data-testid="reset-detection-mode-btn"
                          >
                            <X className="h-4 w-4 mr-1" /> Reset
                          </Button>
                          <Button
                            onClick={saveDetectionMode}
                            disabled={!isDraft || saving}
                            className="bg-[#B89B5E] hover:bg-[#A08050]"
                            data-testid="save-detection-mode-btn"
                          >
                            <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving...' : 'Save Detection Mode'}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </>
              );
            })()}
          </TabsContent>

          {/* Seller Context Tab */}
          <TabsContent value="seller-context" className="space-y-6">
            <Card className="bg-white border-[#E8E6E1]">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-[#B89B5E]" />
                  Company Profile
                </CardTitle>
                <CardDescription>Define your company's identity and market positioning</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Industry</Label>
                    <Input
                      value={profile.seller_industry}
                      onChange={(e) => setProfile(p => ({ ...p, seller_industry: e.target.value }))}
                      placeholder="e.g., Corporate Apparel"
                      disabled={!canEdit}
                      data-testid="industry-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Company Name</Label>
                    <Input
                      value={profile.company_name}
                      onChange={(e) => setProfile(p => ({ ...p, company_name: e.target.value }))}
                      placeholder="e.g., DH Design"
                      disabled={!canEdit}
                      data-testid="company-name-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Value Proposition</Label>
                  {canEdit && (
                    <SectionSuggestButton
                      section="value_proposition"
                      profile={profile}
                      requireFields={['seller_industry', 'company_name', 'target_segments']}
                      onReplaceText={(text) => setProfile(p => ({ ...p, value_proposition: text }))}
                      api={api}
                      testIdPrefix="value-prop"
                      label="✨ Draft value proposition"
                    />
                  )}
                  <Textarea
                    value={profile.value_proposition}
                    onChange={(e) => setProfile(p => ({ ...p, value_proposition: e.target.value }))}
                    placeholder="Describe what makes your company unique..."
                    rows={3}
                    disabled={!canEdit}
                    data-testid="value-proposition-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Market Positioning</Label>
                    <select
                      value={profile.positioning}
                      onChange={(e) => setProfile(p => ({ ...p, positioning: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-sm bg-white"
                      disabled={!canEdit}
                      data-testid="positioning-select"
                    >
                      <option value="premium">Premium</option>
                      <option value="mid-tier">Mid-Tier</option>
                      <option value="value">Value</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Brand Voice</Label>
                    <select
                      value={profile.brand_voice}
                      onChange={(e) => setProfile(p => ({ ...p, brand_voice: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-sm bg-white"
                      disabled={!canEdit}
                      data-testid="brand-voice-select"
                    >
                      <option value="professional">Professional</option>
                      <option value="consultative">Consultative</option>
                      <option value="casual">Casual</option>
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Target Segments */}
            <Card className="bg-white border-[#E8E6E1]">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Target className="h-5 w-5 text-[#B89B5E]" />
                  Target Market Segments
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {profile.target_segments?.map((segment, i) => (
                    <Badge key={i} variant="secondary" className="py-1 px-3">
                      {segment}
                      {canEdit && (
                        <button 
                          onClick={() => removeFromArray('target_segments', i)}
                          className="ml-2 hover:text-red-500"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </Badge>
                  ))}
                </div>
                {canEdit && (
                  <div className="flex gap-2">
                    <Input
                      value={newSegment}
                      onChange={(e) => setNewSegment(e.target.value)}
                      placeholder="Add segment (e.g., hospitality)"
                      onKeyDown={(e) => e.key === 'Enter' && addToArray('target_segments', newSegment, setNewSegment)}
                      data-testid="new-segment-input"
                    />
                    <Button 
                      variant="outline" 
                      onClick={() => addToArray('target_segments', newSegment, setNewSegment)}
                      data-testid="add-segment-btn"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Value Drivers & Differentiators */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="bg-white border-[#E8E6E1]">
                <CardHeader>
                  <CardTitle className="text-base">Value Drivers</CardTitle>
                  <CardDescription>Key benefits you deliver to customers</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {canEdit && (
                    <SectionSuggestButton
                      section="value_drivers"
                      profile={profile}
                      requireFields={['seller_industry', 'target_segments']}
                      onAcceptChip={(text) => setProfile(p => ({
                        ...p,
                        value_drivers: [...new Set([...(p.value_drivers || []), text])]
                      }))}
                      api={api}
                      testIdPrefix="value-drivers"
                      label="✨ Suggest value drivers"
                    />
                  )}
                  {profile.value_drivers?.map((driver, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-[#FAF9F7] rounded-sm">
                      <span className="text-sm">{driver}</span>
                      {canEdit && (
                        <button onClick={() => removeFromArray('value_drivers', i)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  {canEdit && (
                    <div className="flex gap-2">
                      <Input
                        value={newValueDriver}
                        onChange={(e) => setNewValueDriver(e.target.value)}
                        placeholder="Add value driver"
                        onKeyDown={(e) => e.key === 'Enter' && addToArray('value_drivers', newValueDriver, setNewValueDriver)}
                        data-testid="new-value-driver-input"
                      />
                      <Button variant="outline" onClick={() => addToArray('value_drivers', newValueDriver, setNewValueDriver)}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-white border-[#E8E6E1]">
                <CardHeader>
                  <CardTitle className="text-base">Key Differentiators</CardTitle>
                  <CardDescription>What sets you apart from competitors</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {canEdit && (
                    <SectionSuggestButton
                      section="differentiators"
                      profile={profile}
                      requireFields={['seller_industry', 'target_segments']}
                      onAcceptChip={(text) => setProfile(p => ({
                        ...p,
                        differentiators: [...new Set([...(p.differentiators || []), text])]
                      }))}
                      api={api}
                      testIdPrefix="differentiators"
                      label="✨ Suggest differentiators"
                    />
                  )}
                  {profile.differentiators?.map((diff, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-[#FAF9F7] rounded-sm">
                      <span className="text-sm">{diff}</span>
                      {canEdit && (
                        <button onClick={() => removeFromArray('differentiators', i)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  {canEdit && (
                    <div className="flex gap-2">
                      <Input
                        value={newDifferentiator}
                        onChange={(e) => setNewDifferentiator(e.target.value)}
                        placeholder="Add differentiator"
                        onKeyDown={(e) => e.key === 'Enter' && addToArray('differentiators', newDifferentiator, setNewDifferentiator)}
                        data-testid="new-differentiator-input"
                      />
                      <Button variant="outline" onClick={() => addToArray('differentiators', newDifferentiator, setNewDifferentiator)}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Core Problems */}
            <Card className="bg-white border-[#E8E6E1]">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Settings2 className="h-5 w-5 text-[#B89B5E]" />
                  Problems You Solve
                </CardTitle>
                <CardDescription>Customer pain points your products/services address</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {canEdit && (
                  <SectionSuggestButton
                    section="problems"
                    profile={profile}
                    requireFields={['seller_industry', 'target_segments']}
                    onAcceptChip={(text) => setProfile(p => ({
                      ...p,
                      core_problems: [...new Set([...(p.core_problems || []), text])]
                    }))}
                    api={api}
                    testIdPrefix="problems"
                    label="✨ Suggest problems"
                  />
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {profile.core_problems?.map((problem, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-[#FAF9F7] rounded-sm border">
                      <span className="text-sm">{problem}</span>
                      {canEdit && (
                        <button onClick={() => removeFromArray('core_problems', i)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {canEdit && (
                  <div className="flex gap-2">
                    <Input
                      value={newProblem}
                      onChange={(e) => setNewProblem(e.target.value)}
                      placeholder="Add problem you solve"
                      onKeyDown={(e) => e.key === 'Enter' && addToArray('core_problems', newProblem, setNewProblem)}
                      data-testid="new-problem-input"
                    />
                    <Button variant="outline" onClick={() => addToArray('core_problems', newProblem, setNewProblem)}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Signal Trigger Patterns — June 2026 multi-tenant signal filter.
             * Each phrase is a buying-trigger event/keyword that, when found
             * in a news article or LinkedIn post, indicates a company may
             * need this seller's product. Replaces hardcoded industry
             * pattern lists so every industry self-serves their relevance.
             */}
            <Card className="bg-white border-[#E8E6E1]" data-testid="trigger-patterns-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Settings2 className="h-5 w-5 text-[#B89B5E]" />
                  Signal Trigger Patterns
                </CardTitle>
                <CardDescription>
                  Buying-trigger phrases that tell the Signal Engine to surface a news item for this seller. The engine matches these against article titles + snippets. Be specific (e.g. &ldquo;expands sales team&rdquo;, &ldquo;new chief revenue officer&rdquo;, &ldquo;series B funding&rdquo;).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {canEdit && (
                  <SectionSuggestButton
                    section="trigger_patterns"
                    profile={profile}
                    requireFields={['seller_industry', 'target_segments']}
                    onAcceptChip={(text) => setProfile(p => ({
                      ...p,
                      trigger_patterns: [...new Set([...(p.trigger_patterns || []), text.toLowerCase()])]
                    }))}
                    api={api}
                    testIdPrefix="trigger-patterns"
                    label="✨ Suggest trigger patterns"
                  />
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {(profile.trigger_patterns || []).map((pat, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between px-3 py-2 bg-[#FAF9F7] rounded-sm border text-sm"
                      data-testid={`trigger-pattern-${i}`}
                    >
                      <span className="font-mono text-xs text-[#3D3830] truncate">{pat}</span>
                      {canEdit && (
                        <button
                          onClick={() => removeFromArray('trigger_patterns', i)}
                          className="text-red-500 hover:text-red-700 ml-2 flex-shrink-0"
                          data-testid={`trigger-pattern-remove-${i}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {(!profile.trigger_patterns || profile.trigger_patterns.length === 0) && (
                  <div className="text-xs text-[#8A8070] italic p-3 bg-[#FAF9F7] rounded-sm">
                    No trigger patterns yet. Click <strong>✨ Suggest trigger patterns</strong> above to have AI generate them from your industry and target segments, or add your own below.
                  </div>
                )}
                {canEdit && (
                  <div className="flex gap-2">
                    <Input
                      value={newTriggerPattern}
                      onChange={(e) => setNewTriggerPattern(e.target.value)}
                      placeholder="Add trigger phrase (e.g. 'expands sales team')"
                      onKeyDown={(e) => e.key === 'Enter' && addToArray('trigger_patterns', newTriggerPattern.toLowerCase(), setNewTriggerPattern)}
                      data-testid="new-trigger-pattern-input"
                    />
                    <Button
                      variant="outline"
                      onClick={() => addToArray('trigger_patterns', newTriggerPattern.toLowerCase(), setNewTriggerPattern)}
                      data-testid="add-trigger-pattern-btn"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {canEdit && (
              <div className="space-y-4">
                {contextStatus && (
                  <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${contextStatus.detection_mode === 'company' ? 'bg-purple-500' : 'bg-blue-500'}`} />
                          <div>
                            <p className="text-sm font-medium">
                              Detection Mode: <span className="text-purple-700">{contextStatus.detection_mode?.toUpperCase()}</span>
                            </p>
                            <p className="text-xs text-gray-500">
                              {contextStatus.detection_mode === 'company' 
                                ? 'AI is looking for B2B company signals (sales hiring, territory expansion, etc.)'
                                : 'AI is looking for property signals (hotel openings, renovations, etc.)'}
                            </p>
                          </div>
                        </div>
                        <Badge variant="outline" className={contextStatus.detection_mode === 'company' ? 'border-purple-300 text-purple-700' : 'border-blue-300 text-blue-700'}>
                          {contextStatus.target_segments?.length || 0} target segments
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Action Buttons */}
                <div className="flex justify-end gap-3">
                  <Button 
                    onClick={reprocessSignals} 
                    disabled={reprocessing}
                    variant="outline"
                    className="border-purple-300 text-purple-700 hover:bg-purple-50"
                    data-testid="reprocess-signals-btn"
                  >
                    {reprocessing ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                    Reprocess Signals with Current Context
                  </Button>
                  <Button 
                    onClick={saveProfile} 
                    disabled={saving}
                    className="bg-[#1A1A1A] hover:bg-[#2A2A2A]"
                    data-testid="save-seller-context-btn"
                  >
                    {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Seller Context
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Products & Tiers Tab */}
          <TabsContent value="products" className="space-y-6">
            {/* Products */}
            <Card className="bg-white border-[#E8E6E1]">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package className="h-5 w-5 text-[#B89B5E]" />
                  Products & Services
                </CardTitle>
                <CardDescription>Define your product catalog for AI recommendations</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {profile.products?.map((product, i) => (
                    <div key={i} className="p-4 bg-[#FAF9F7] rounded-sm border relative group">
                      <div className="font-medium text-[#1A1A1A]">{product.name}</div>
                      {product.category && (
                        <Badge variant="outline" className="mt-1">{product.category}</Badge>
                      )}
                      {product.description && (
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{product.description}</p>
                      )}
                      {product.value_drivers?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {product.value_drivers.slice(0, 2).map((vd, vi) => (
                            <Badge key={vi} variant="secondary" className="text-xs">{vd}</Badge>
                          ))}
                        </div>
                      )}
                      {canEdit && (
                        <button 
                          onClick={() => removeProduct(i)}
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-red-500"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                
                {canEdit && (
                  <div className="p-4 border-2 border-dashed rounded-sm bg-[#FAF9F7]/50">
                    <div className="text-sm font-medium mb-3">Add New Product</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Input
                        value={newProduct.name}
                        onChange={(e) => setNewProduct(p => ({ ...p, name: e.target.value }))}
                        placeholder="Product name *"
                        data-testid="new-product-name"
                      />
                      <Input
                        value={newProduct.category}
                        onChange={(e) => setNewProduct(p => ({ ...p, category: e.target.value }))}
                        placeholder="Category (e.g., Uniforms)"
                      />
                    </div>
                    <Textarea
                      value={newProduct.description}
                      onChange={(e) => setNewProduct(p => ({ ...p, description: e.target.value }))}
                      placeholder="Brief description"
                      rows={2}
                      className="mt-3"
                    />
                    <Button 
                      onClick={addProduct} 
                      variant="outline" 
                      className="mt-3"
                      disabled={!newProduct.name}
                      data-testid="add-product-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" /> Add Product
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Product Tiers */}
            <Card className="bg-white border-[#E8E6E1]">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Layers className="h-5 w-5 text-[#B89B5E]" />
                  Product Tiers
                </CardTitle>
                <CardDescription>Define pricing tiers for AI to recommend appropriate solutions</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {profile.product_tiers?.map((tier, i) => (
                    <div key={i} className="p-4 bg-gradient-to-br from-white to-[#FAF9F7] rounded-sm border relative group">
                      <div className="font-medium text-[#1A1A1A]">{tier.name}</div>
                      {tier.price_range && (
                        <div className="text-[#B89B5E] text-sm font-medium mt-1">{tier.price_range}</div>
                      )}
                      {tier.description && (
                        <p className="text-sm text-muted-foreground mt-2">{tier.description}</p>
                      )}
                      {tier.ideal_for && (
                        <p className="text-xs text-muted-foreground mt-2 italic">Ideal for: {tier.ideal_for}</p>
                      )}
                      {canEdit && (
                        <button 
                          onClick={() => removeTier(i)}
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-red-500"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                
                {canEdit && (
                  <div className="p-4 border-2 border-dashed rounded-sm bg-[#FAF9F7]/50">
                    <div className="text-sm font-medium mb-3">Add New Tier</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Input
                        value={newTier.name}
                        onChange={(e) => setNewTier(t => ({ ...t, name: e.target.value }))}
                        placeholder="Tier name (e.g., Premium) *"
                        data-testid="new-tier-name"
                      />
                      <Input
                        value={newTier.price_range}
                        onChange={(e) => setNewTier(t => ({ ...t, price_range: e.target.value }))}
                        placeholder="Price range (e.g., $50K-$150K)"
                      />
                      <Input
                        value={newTier.description}
                        onChange={(e) => setNewTier(t => ({ ...t, description: e.target.value }))}
                        placeholder="Description"
                      />
                      <Input
                        value={newTier.ideal_for}
                        onChange={(e) => setNewTier(t => ({ ...t, ideal_for: e.target.value }))}
                        placeholder="Ideal for (e.g., Luxury hotels)"
                      />
                    </div>
                    <Button 
                      onClick={addTier} 
                      variant="outline" 
                      className="mt-3"
                      disabled={!newTier.name}
                      data-testid="add-tier-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" /> Add Tier
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {canEdit && (
              <div className="flex justify-end">
                <Button 
                  onClick={saveProfile} 
                  disabled={saving}
                  className="bg-[#1A1A1A] hover:bg-[#2A2A2A]"
                >
                  {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Products & Tiers
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Buyer Intelligence Tab */}
          <TabsContent value="buyers" className="space-y-6">
            <Card className="bg-white border-[#E8E6E1]">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="h-5 w-5 text-[#B89B5E]" />
                  Typical Buyer Roles
                </CardTitle>
                <CardDescription>Define the decision makers AI should consider</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {canEdit && (
                  <SectionSuggestButton
                    section="buyer_roles"
                    profile={profile}
                    requireFields={['seller_industry', 'target_segments']}
                    onAcceptRole={(role) => setProfile(p => ({
                      ...p,
                      buyer_roles: [...(p.buyer_roles || []), role]
                    }))}
                    api={api}
                    testIdPrefix="buyer-roles"
                    label="✨ Suggest buyer roles"
                  />
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {profile.buyer_roles?.map((role, i) => (
                    <div key={i} className="p-4 bg-[#FAF9F7] rounded-sm border relative group">
                      <div className="font-medium text-[#1A1A1A]">{role.title}</div>
                      {role.priorities?.length > 0 && (
                        <div className="mt-2">
                          <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Priorities</div>
                          <div className="flex flex-wrap gap-1">
                            {role.priorities.map((p, pi) => (
                              <Badge key={pi} variant="secondary" className="text-xs">{p}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {role.pain_points?.length > 0 && (
                        <div className="mt-2">
                          <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Pain Points</div>
                          <div className="flex flex-wrap gap-1">
                            {role.pain_points.map((p, pi) => (
                              <Badge key={pi} variant="outline" className="text-xs text-red-600">{p}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {canEdit && (
                        <button 
                          onClick={() => removeBuyerRole(i)}
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-red-500"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                
                {canEdit && (
                  <div className="p-4 border-2 border-dashed rounded-sm bg-[#FAF9F7]/50">
                    <div className="text-sm font-medium mb-3">Add New Buyer Role</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <Input
                        value={newBuyerRole.title}
                        onChange={(e) => setNewBuyerRole(r => ({ ...r, title: e.target.value }))}
                        placeholder="Role title (e.g., General Manager) *"
                        data-testid="new-buyer-title"
                      />
                      <Input
                        value={newBuyerRole.priorities.join(', ')}
                        onChange={(e) => setNewBuyerRole(r => ({ 
                          ...r, 
                          priorities: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                        }))}
                        placeholder="Priorities (comma-separated)"
                      />
                      <Input
                        value={newBuyerRole.pain_points.join(', ')}
                        onChange={(e) => setNewBuyerRole(r => ({ 
                          ...r, 
                          pain_points: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                        }))}
                        placeholder="Pain points (comma-separated)"
                      />
                    </div>
                    <Button 
                      onClick={addBuyerRole} 
                      variant="outline" 
                      className="mt-3"
                      disabled={!newBuyerRole.title}
                      data-testid="add-buyer-role-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" /> Add Buyer Role
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {canEdit && (
              <div className="flex justify-end">
                <Button 
                  onClick={saveProfile} 
                  disabled={saving}
                  className="bg-[#1A1A1A] hover:bg-[#2A2A2A]"
                >
                  {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Buyer Intelligence
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Knowledge Sources Tab */}
          <TabsContent value="knowledge" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
              <Card className="bg-gradient-to-br from-[#B89B5E]/10 to-transparent border-[#B89B5E]/20">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-[#B89B5E]/10">
                      <FileText className="h-5 w-5 text-[#B89B5E]" />
                    </div>
                    <div>
                      <div className="text-2xl font-semibold">{knowledgeSources.filter(s => s.source_type === 'pdf').length}</div>
                      <div className="text-xs text-muted-foreground">PDF Documents</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-blue-50 to-transparent border-blue-100">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-100">
                      <Globe className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-semibold">{knowledgeSources.filter(s => s.source_type === 'url').length}</div>
                      <div className="text-xs text-muted-foreground">Website URLs</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-purple-50 to-transparent border-purple-100">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-purple-100">
                      <Image className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-semibold">{knowledgeSources.filter(s => s.source_type === 'image').length}</div>
                      <div className="text-xs text-muted-foreground">Images</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-green-50 to-transparent border-green-100">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-100">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-semibold">{knowledgeSources.filter(s => s.status === 'indexed').length}</div>
                      <div className="text-xs text-muted-foreground">Indexed Sources</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Upload Section */}
            {canEdit && (
              <Card className="bg-white border-[#E8E6E1]">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Upload className="h-5 w-5 text-[#B89B5E]" />
                    Add Knowledge Sources
                  </CardTitle>
                  <CardDescription>Upload PDFs, images, or add website URLs to train your AI</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* PDF Upload */}
                    <div className="p-4 border-2 border-dashed rounded-sm bg-[#FAF9F7]/50">
                      <div className="text-center">
                        <FileText className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                        <div className="text-sm font-medium mb-1">Upload PDF</div>
                        <p className="text-xs text-muted-foreground mb-3">Catalogs, spec sheets, brochures</p>
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept=".pdf"
                            onChange={handleFileUpload}
                            className="hidden"
                            disabled={uploading}
                            data-testid="pdf-upload-input"
                          />
                          <Button variant="outline" size="sm" disabled={uploading} asChild>
                            <span>
                              {uploading ? (
                                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <Upload className="h-4 w-4 mr-2" />
                              )}
                              Choose PDF
                            </span>
                          </Button>
                        </label>
                      </div>
                    </div>

                    {/* Image Upload */}
                    <div className="p-4 border-2 border-dashed rounded-sm bg-[#FAF9F7]/50">
                      <div className="text-center">
                        <Image className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                        <div className="text-sm font-medium mb-1">Upload Image</div>
                        <p className="text-xs text-muted-foreground mb-3">Screenshots, infographics, flyers</p>
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept=".png,.jpg,.jpeg,.webp"
                            onChange={handleImageUpload}
                            className="hidden"
                            disabled={uploading}
                            data-testid="image-upload-input"
                          />
                          <Button variant="outline" size="sm" disabled={uploading} asChild>
                            <span>
                              {uploading ? (
                                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <Image className="h-4 w-4 mr-2" />
                              )}
                              Choose Image
                            </span>
                          </Button>
                        </label>
                      </div>
                    </div>

                    {/* URL Input */}
                    <div className="p-4 border-2 border-dashed rounded-sm bg-[#FAF9F7]/50">
                      <div className="text-center mb-2">
                        <Globe className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                        <div className="text-sm font-medium mb-1">Add Website URL</div>
                        <p className="text-xs text-muted-foreground mb-3">Company or product pages</p>
                      </div>
                      <div className="space-y-2">
                        <Input
                          value={newUrl.name}
                          onChange={(e) => setNewUrl(u => ({ ...u, name: e.target.value }))}
                          placeholder="Source name"
                          className="text-sm"
                          data-testid="url-name-input"
                        />
                        <Input
                          value={newUrl.url}
                          onChange={(e) => setNewUrl(u => ({ ...u, url: e.target.value }))}
                          placeholder="https://example.com"
                          className="text-sm"
                          data-testid="url-input"
                        />
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="w-full"
                          onClick={addUrlSource}
                          disabled={uploading || !newUrl.name || !newUrl.url}
                          data-testid="add-url-btn"
                        >
                          {uploading ? (
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Link className="h-4 w-4 mr-2" />
                          )}
                          Add URL
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Knowledge Sources List */}
            <Card className="bg-white border-[#E8E6E1]">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <BookOpen className="h-5 w-5 text-[#B89B5E]" />
                      Knowledge Library
                    </CardTitle>
                    <CardDescription>Select sources to extract intelligence from</CardDescription>
                  </div>
                  {/* Extraction Button */}
                  {knowledgeSources.length > 0 && (
                    <Button
                      onClick={extractFromSources}
                      disabled={extracting || selectedSourcesForExtraction.length === 0}
                      className="bg-gradient-to-r from-[#B89B5E] to-[#9A7B3E] hover:from-[#A08050] hover:to-[#8A6B2E] text-white"
                      data-testid="extract-intelligence-btn"
                    >
                      {extracting ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Extracting...
                        </>
                      ) : (
                        <>
                          <Wand2 className="h-4 w-4 mr-2" />
                          Extract Intelligence
                          {selectedSourcesForExtraction.length > 0 && (
                            <Badge variant="secondary" className="ml-2 bg-white/20">{selectedSourcesForExtraction.length}</Badge>
                          )}
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {knowledgeSources.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <BookOpen className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No knowledge sources added yet</p>
                    <p className="text-sm">Upload PDFs, images, or add URLs to get started</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Select All */}
                    <div className="flex items-center gap-2 pb-2 border-b">
                      <Checkbox
                        id="select-all-sources"
                        checked={selectedSourcesForExtraction.length === knowledgeSources.filter(s => s.status === 'indexed').length && knowledgeSources.filter(s => s.status === 'indexed').length > 0}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedSourcesForExtraction(knowledgeSources.filter(s => s.status === 'indexed').map(s => s.id));
                          } else {
                            setSelectedSourcesForExtraction([]);
                          }
                        }}
                      />
                      <Label htmlFor="select-all-sources" className="text-sm text-muted-foreground cursor-pointer">
                        Select all indexed sources
                      </Label>
                    </div>
                    
                    {knowledgeSources.map((source) => (
                      <div 
                        key={source.id} 
                        className={`flex items-center justify-between p-4 rounded-sm border transition-colors ${
                          selectedSourcesForExtraction.includes(source.id) 
                            ? 'bg-[#B89B5E]/5 border-[#B89B5E]/30' 
                            : 'bg-[#FAF9F7]'
                        }`}
                        data-testid={`knowledge-source-${source.id}`}
                      >
                        <div className="flex items-center gap-3">
                          {/* Selection Checkbox */}
                          <Checkbox
                            id={`source-${source.id}`}
                            checked={selectedSourcesForExtraction.includes(source.id)}
                            onCheckedChange={() => toggleSourceSelection(source.id)}
                            disabled={source.status !== 'indexed'}
                          />
                          
                          <div className={`p-2 rounded-lg ${
                            source.source_type === 'pdf' 
                              ? 'bg-[#B89B5E]/10' 
                              : source.source_type === 'image' 
                                ? 'bg-purple-100' 
                                : 'bg-blue-100'
                          }`}>
                            {source.source_type === 'pdf' ? (
                              <FileText className="h-5 w-5 text-[#B89B5E]" />
                            ) : source.source_type === 'image' ? (
                              <Image className="h-5 w-5 text-purple-600" />
                            ) : (
                              <Globe className="h-5 w-5 text-blue-600" />
                            )}
                          </div>
                          <div>
                            <div className="font-medium text-[#1A1A1A]">{source.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {source.source_type === 'image' 
                                ? 'Image file • ' 
                                : source.total_chunks > 0 
                                  ? `${source.total_chunks} chunks • ` 
                                  : ''
                              }
                              {source.total_words > 0 && `${source.total_words.toLocaleString()} words • `}
                              Added {new Date(source.created_at).toLocaleDateString()}
                            </div>
                            {source.url && (
                              <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline">
                                {source.url}
                              </a>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {getStatusBadge(source.status)}
                          {canEdit && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => reprocessSource(source.id)}
                                title="Reprocess"
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => deleteSource(source.id)}
                                className="text-red-500 hover:text-red-700"
                                data-testid={`delete-source-${source.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Extraction Draft Modal */}
      <Dialog open={showExtractionModal} onOpenChange={setShowExtractionModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-[#B89B5E]" />
              Extracted Intelligence
              <Badge variant="outline" className="ml-2 text-amber-600 border-amber-300 bg-amber-50">DRAFT</Badge>
            </DialogTitle>
            <DialogDescription>
              Review these suggestions extracted from your knowledge sources. Click sections to apply them to your profile.
              <strong className="text-amber-700 block mt-1">Nothing is saved until you click "Save" on the main profile.</strong>
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="flex-1 pr-4">
            {extractionDraft && (
              <div className="space-y-4 py-4">
                {/* Confidence & Status */}
                <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                  <Badge className={`${
                    extractionDraft.confidenceScore === 'high' 
                      ? 'bg-green-100 text-green-700' 
                      : extractionDraft.confidenceScore === 'medium'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-red-100 text-red-700'
                  }`}>
                    {extractionDraft.confidenceScore?.toUpperCase()} Confidence
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    Status: {extractionDraft.extractionStatus}
                  </span>
                  {extractionDraft.mergedFrom && (
                    <span className="text-sm text-muted-foreground">
                      • Merged from {extractionDraft.mergedFrom.length} sources
                    </span>
                  )}
                </div>
                
                {/* Flagged Items */}
                {extractionDraft.flaggedItems?.length > 0 && (
                  <Card className="border-amber-200 bg-amber-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2 text-amber-800">
                        <AlertCircle className="h-4 w-4" />
                        Items Requiring Review ({extractionDraft.flaggedItems.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {extractionDraft.flaggedItems.map((item, idx) => (
                          <div key={idx} className="text-sm p-2 bg-white rounded border border-amber-200">
                            <span className="font-medium text-amber-700">{item.tag || '[FLAG]'}</span>
                            {item.field && <span className="text-muted-foreground"> - {item.field}</span>}
                            {item.value1 && item.value2 && (
                              <div className="mt-1 text-xs text-muted-foreground">
                                Option 1: {item.value1}<br/>
                                Option 2: {item.value2}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Seller Context */}
                {extractionDraft.sellerContext && Object.values(extractionDraft.sellerContext).some(v => v) && (
                  <Card>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm">Seller Context</CardTitle>
                        <Button size="sm" variant="outline" onClick={() => applyDraftSection('sellerContext', extractionDraft.sellerContext)}>
                          <Check className="h-3 w-3 mr-1" /> Apply
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="text-sm space-y-1">
                      {extractionDraft.sellerContext.companyName && <div><strong>Company:</strong> {extractionDraft.sellerContext.companyName}</div>}
                      {extractionDraft.sellerContext.sellerIndustry && <div><strong>Industry:</strong> {extractionDraft.sellerContext.sellerIndustry}</div>}
                      {extractionDraft.sellerContext.valueProposition && <div><strong>Value Prop:</strong> {extractionDraft.sellerContext.valueProposition}</div>}
                      {extractionDraft.sellerContext.brandVoice && <div><strong>Brand Voice:</strong> {extractionDraft.sellerContext.brandVoice}</div>}
                      {extractionDraft.sellerContext.marketPositioning && <div><strong>Positioning:</strong> {extractionDraft.sellerContext.marketPositioning}</div>}
                    </CardContent>
                  </Card>
                )}
                
                {/* Products */}
                {extractionDraft.products?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm">Products ({extractionDraft.products.length})</CardTitle>
                        <Button size="sm" variant="outline" onClick={() => applyDraftSection('products', extractionDraft.products)}>
                          <Check className="h-3 w-3 mr-1" /> Apply All
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {extractionDraft.products.map((p, idx) => (
                          <Badge key={idx} variant="secondary">{toDisplayString(p) || `Product ${idx + 1}`}</Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Target Segments */}
                {extractionDraft.targetSegments?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm">Target Segments ({extractionDraft.targetSegments.length})</CardTitle>
                        <Button size="sm" variant="outline" onClick={() => applyDraftSection('targetSegments', extractionDraft.targetSegments)}>
                          <Check className="h-3 w-3 mr-1" /> Apply All
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {extractionDraft.targetSegments.map((s, idx) => (
                          <Badge key={idx} variant="outline">{toDisplayString(s) || `Segment ${idx + 1}`}</Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Buyer Roles */}
                {extractionDraft.buyerRoles?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm">Buyer Roles ({extractionDraft.buyerRoles.length})</CardTitle>
                        <Button size="sm" variant="outline" onClick={() => applyDraftSection('buyerRoles', extractionDraft.buyerRoles)}>
                          <Check className="h-3 w-3 mr-1" /> Apply All
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {extractionDraft.buyerRoles.map((r, idx) => (
                          <Badge key={idx} variant="secondary">{toDisplayString(r) || `Role ${idx + 1}`}</Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Value Drivers */}
                {extractionDraft.valueDrivers?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm">Value Drivers ({extractionDraft.valueDrivers.length})</CardTitle>
                        <Button size="sm" variant="outline" onClick={() => applyDraftSection('valueDrivers', extractionDraft.valueDrivers)}>
                          <Check className="h-3 w-3 mr-1" /> Apply All
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {extractionDraft.valueDrivers.map((v, idx) => (
                          <Badge key={idx} variant="outline" className="bg-green-50">{toDisplayString(v) || `Driver ${idx + 1}`}</Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Problems Solved */}
                {extractionDraft.problemsSolved?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm">Problems Solved ({extractionDraft.problemsSolved.length})</CardTitle>
                        <Button size="sm" variant="outline" onClick={() => applyDraftSection('problemsSolved', extractionDraft.problemsSolved)}>
                          <Check className="h-3 w-3 mr-1" /> Apply All
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ul className="text-sm list-disc list-inside space-y-1">
                        {extractionDraft.problemsSolved.map((p, idx) => (
                          <li key={idx}>{toDisplayString(p) || `Problem ${idx + 1}`}</li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
                
                {/* Key Differentiators */}
                {extractionDraft.keyDifferentiators?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm">Key Differentiators ({extractionDraft.keyDifferentiators.length})</CardTitle>
                        <Button size="sm" variant="outline" onClick={() => applyDraftSection('differentiators', extractionDraft.keyDifferentiators)}>
                          <Check className="h-3 w-3 mr-1" /> Apply All
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ul className="text-sm list-disc list-inside space-y-1">
                        {extractionDraft.keyDifferentiators.map((d, idx) => (
                          <li key={idx}>{toDisplayString(d) || `Differentiator ${idx + 1}`}</li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
                
                {/* Detection Mode Suggestion */}
                {extractionDraft.detectionModeSuggestion && (
                  <Card className="border-[#B89B5E]/30 bg-[#B89B5E]/5">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Compass className="h-4 w-4 text-[#B89B5E]" />
                          Suggested Detection Mode
                        </CardTitle>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid="apply-detection-mode-suggestion-btn"
                          onClick={() => acceptModeSuggestion(
                            extractionDraft.detectionModeSuggestion.suggestedMode,
                            extractionDraft.detectionModeSuggestion.rationale,
                            extractionDraft.detectionModeSuggestion.confidence
                          )}
                        >
                          <Check className="h-3 w-3 mr-1" /> Use Suggestion
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-3">
                        <Badge className="bg-[#B89B5E] text-white">
                          {extractionDraft.detectionModeSuggestion.suggestedMode?.toUpperCase()}
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          {extractionDraft.detectionModeSuggestion.confidence} confidence
                        </span>
                      </div>
                      {extractionDraft.detectionModeSuggestion.rationale && (
                        <p className="text-sm mt-2 text-muted-foreground">
                          {extractionDraft.detectionModeSuggestion.rationale}
                        </p>
                      )}
                      <p className="text-xs mt-2 text-[#B89B5E]">
                        This is a suggestion only. You can confirm or override on the Detection Mode tab.
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </ScrollArea>
          
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={discardExtractionDraft}>
              <X className="h-4 w-4 mr-2" />
              Discard Draft
            </Button>
            <Button onClick={applyAllDraftSections} className="bg-[#B89B5E] hover:bg-[#A08050]">
              <Check className="h-4 w-4 mr-2" />
              Apply All Sections
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
